/**
 * interruption — the SINGLE definition of "this conversation was interrupted".
 *
 * Lives under src/llm (next to LLMClient) rather than under pages/workspace so BOTH
 * surfaces can import it. It began as a port of the legacy chat's detector
 * (ChatSession.js#turnsInterrupted + endsInterrupted), and PR #29 review flagged
 * the real risk in having two copies: the next turn-shape change lands in one
 * and not the other.
 *
 * Both chats use it: the classic ChatSession since the PLEX-5967 follow-up, which
 * replaced its local turnsInterrupted/endsInterrupted. interruption.parity.test.js
 * keeps v1's rules as they were, so the differences below stay deliberate.
 *
 * Only the TOP level being stuck makes a conversation interrupted. A sub-agent
 * runs inside its parent's tool call (rd tools.py run_agent; it can't outlive the
 * turn, PLEX-6259), so once that call has a result, a sub-agent whose own turns end
 * mid-flight is left over from an earlier interruption: Resume continues from the
 * failed result and keeps those turns. v1 counted such a frame (and so did this
 * module, until the PLEX-5967 follow-up), offering Resume on a finished
 * conversation; replayFrom(last) then dropped the final answer and sent nothing.
 * Sub-agent turns only decide WHERE a stuck top level is stuck (interruptionSite).
 *
 * Other differences from v1:
 *   - Stop-reason field. v1 reads only `stopReason`; this reads
 *     `stopReason || stop_reason`, because v2's Turn.js accepts both the
 *     streamed and the persisted/API-shaped spelling.
 *   - Nesting. v1 looked one level into client.subAgentTurns; v2 nests sub-agent
 *     turns in turn.agentMessagesCache, so the site walk follows the stuck call down
 *     through either shape.
 */

import { ROLE_USER, StopReason } from "./LLMClient";

// v2 Turn stores the stop reason under either `stopReason` (streamed) or
// `stop_reason` (some persisted / API-shaped records) — Turn.js reads both, so
// detection must too.
const stopReasonOf = (turn) => turn?.stopReason || turn?.stop_reason || "";

// Is a single turns array interrupted at its TAIL (cases 1 & 2 above)?
const tailInterrupted = (turns) => {
  if (!Array.isArray(turns) || turns.length === 0) return false;
  const last = turns[turns.length - 1];
  if (!last) return false;
  const blocks = Array.isArray(last.content) ? last.content : [];
  // Case 1: user turn awaiting the LLM (tool result present, no text yet).
  if (
    last.role === ROLE_USER &&
    blocks.some((b) => b && b.toolResult) &&
    !blocks.some((b) => b && b.text)
  ) {
    return true;
  }
  // Case 2: assistant stopped to run a tool that never returned.
  if (stopReasonOf(last) === StopReason.TOOL_USE) return true;
  return false;
};

// The calls a stuck tail is waiting on: an assistant turn's tool calls, or the calls
// a tool-result turn answers (the loader writes a "session was interrupted" result
// into a call that never returned, and resuming continues from it).
const pendingCallIds = (turns) => {
  const blocks = turns[turns.length - 1]?.content || [];
  return blocks
    .map((b) => b?.toolUse?.toolUseId || b?.toolResult?.toolUseId)
    .filter(Boolean);
};

// A sub-agent call's turns: on the turn that made the call (agentMessagesCache,
// persisted, v2) or in the client's flat map (client.subAgentTurns, the classic
// chat's shape and a live session's).
const subAgentTurnsFor = (turns, toolUseId, subAgentTurns) => {
  for (const turn of turns) {
    const cached = turn?.agentMessagesCache?.[toolUseId];
    if (cached) return cached;
  }
  if (!subAgentTurns) return null;
  // The client's own map is a MobX observable map, which isn't `instanceof Map`.
  return typeof subAgentTurns.get === "function"
    ? subAgentTurns.get(toolUseId) || null
    : subAgentTurns[toolUseId] || null;
};

// Name the tool that opened a sub-agent frame, for user-facing copy. Best
// effort: the cache key is the toolUseId, so find the block that matches.
const toolNameFor = (turns, toolUseId) => {
  if (!Array.isArray(turns)) return null;
  for (const turn of turns) {
    for (const block of turn?.content || []) {
      if (block?.toolUse?.toolUseId === toolUseId) return block.toolUse.name || null;
    }
  }
  return null;
};

/**
 * WHERE a conversation is interrupted — not just whether.
 *
 * This distinction is load-bearing, because the two sites need different
 * handling and only one of them is fixable in the front end:
 *
 *   scope "top"      the top-level conversation stopped at a tool boundary.
 *                    client.replayFrom(lastIndex) continues it correctly:
 *                    the turns are the submitted history, so re-sending them
 *                    picks up exactly where the stream died.
 *
 *   scope "subagent" the call the top level is stuck on opened a sub-agent
 *                    (`run_agent`) that was itself cut off.
 *                    replayFrom CANNOT continue this. Sub-agents are spawned
 *                    and owned inside the BACKEND's tool loop
 *                    (rd/search/plex/search/llm/tools.py, run_agent); their
 *                    message history is a server-side frame, not part of the
 *                    `turns` array the client submits, and the backend exposes
 *                    only status/snapshot/cancel per agent_id — no continue.
 *                    So resuming re-issues the parent's tool call, which
 *                    restarts the sub-agent from zero and discards whatever it
 *                    had finished.
 *
 * PR #30 review (Tim): "we would need to bubble up the continuation, manually
 * incorporating tool results to the layer above until the top agent reaches
 * end_turn." That is the right design, and it is a BACKEND capability: the tool
 * loop has to be able to resume a partially-completed run_agent frame, or at
 * minimum accept an injected tool result for an unfinished sub-agent so the
 * parent can proceed to end_turn. Nothing the client can synthesize gets there,
 * because the client never held the sub-agent's history in the first place.
 *
 * Until that exists, the honest front-end behaviour is to still offer resume —
 * re-running the sub-agent does recover the conversation — but to say so, so a
 * user whose long sub-agent search just died isn't left watching an unexplained
 * repeat of the wait that already failed once.
 *
 * @returns {null|{scope: "top"|"subagent", toolUseId?: string,
 *                 toolName?: string|null, depth?: number}}
 */
export const interruptionSite = (turns, subAgentTurns = null) => {
  if (!tailInterrupted(turns)) return null;
  // Follow the stuck call down while the sub-agent it opened was itself cut off, so the
  // reported depth is the deepest frame actually stuck. Each call is followed once: the
  // flat map is keyed by call id, so a malformed frame naming its own id would loop.
  const followed = new Set();
  const cutOff = (list, depth) => {
    for (const toolUseId of pendingCallIds(list)) {
      if (followed.has(toolUseId)) continue;
      followed.add(toolUseId);
      const sub = subAgentTurnsFor(list, toolUseId, subAgentTurns);
      if (!tailInterrupted(sub)) continue;
      return cutOff(sub, depth + 1) || {
        scope: "subagent",
        toolUseId,
        toolName: toolNameFor(list, toolUseId),
        depth,
      };
    }
    return null;
  };
  return cutOff(turns, 1) || {scope: "top"};
};

/**
 * @param {Array}          turns            the conversation's top-level turns
 *                                          (client.turns).
 * @param {Map|Object|null} subAgentTurns the client-level flat map of
 *                                          sub-agent conversations
 *                                          (client.subAgentTurns). Optional;
 *                                          it decides only where a stuck top
 *                                          level is stuck, never whether.
 * @returns {boolean}
 */
export const isConversationInterrupted = (turns, subAgentTurns = null) =>
  interruptionSite(turns, subAgentTurns) !== null;

export default isConversationInterrupted;
